import { ZodObject, ZodRawShape, ZodType, z } from "zod";

const createAssetSchema = () =>
  z.object({
    id: z.number().int().nullable(),
    alt: z
      .string()
      .nullish()
      .transform((x) => x ?? ""),
    name: z
      .string()
      .nullish()
      .transform((x) => x ?? ""),
    title: z
      .string()
      .nullish()
      .transform((x) => x ?? ""),
    source: z
      .string()
      .nullish()
      .transform((x) => x ?? ""),
    filename: z.string().url(),
    copyright: z
      .string()
      .nullish()
      .transform((x) => x ?? ""),
    is_external_url: z.boolean(),
  });

const createColorSchema = () =>
  z
    .object({
      color: z.string(),
    })
    .optional();

const createLinkSchema = () =>
  z.intersection(
    z.object({
      target: z.string().optional(),
    }),
    z.union([
      z.object({
        linktype: z.literal("url"),
        url: z.string().url(),
      }),
      z.object({
        linktype: z.literal("story"),
        cached_url: z.string(),
      }),
    ])
  );

const createJobPostingSchema = () =>
  z
    .array(
      z.intersection(
        createComponentBaseSchema("job-posting"),
        z
          .object({
            date_posted: z.coerce.date().or(z.literal("")),
            employer_overview: z.string(),
            employment_type: z.string(),
            estimated_salary: z.coerce.number().pipe(z.number()),
            industry: z.string(),
            job_benefits: z.string(),
            job_immediate_start: z.boolean(),
            job_location: z.string(),
            job_location_type: z.string(),
            job_start_date: z.coerce.date().or(z.literal("")),
            physical_requirements: z.string(),
            qualifications: z.string(),
            responsibilities: z.string(),
            skills: z.string(),
            title: z.string(),
            description: z.string(),
            hiring_organization: z.string(),
            hiring_organization_logo: createAssetSchema(),
            total_job_openings: z.coerce.number().pipe(z.number().int()),
            valid_through: z.coerce.date().or(z.literal("")),
            work_hours: z.string(),
          })
          .partial()
      )
    )
    .max(1)
    .min(0)
    .default([])
    .transform((x) => x[0]);

const createMenuItemSchema = () =>
  z.object({
    ...createComponentBaseSchema("menu-item").shape,
    label: z.string(),
    link: createLinkSchema().optional(),
    anchor: z.string().optional(),
  });

const createRichTextSchema = () =>
  z.object({
    type: z.literal("doc"),
    content: z.array(z.any()),
  });

const createComponentBaseSchema = <T extends string>(componentName: T) =>
  z.object({
    _uid: z.string().uuid(),
    _editable: z.string().optional(),
    component: z.literal(componentName),
  });

const createSeoSchema = () =>
  z
    .array(
      z.object({
        ...createComponentBaseSchema("seo").shape,
        image: createAssetSchema(),
        title: z.string(),
        description: z.string(),
        favicon: createAssetSchema(),
      })
    )
    .max(1)
    .min(1)
    .transform((x) => x[0]);

const createGtmSchema = () =>
  z
    .array(
      z.object({
        ...createComponentBaseSchema("gtm").shape,
        containerid: z.string(),
      })
    )
    .max(1)
    .min(0)
    .default([])
    .transform((x) => x[0]);

const createButtonBaseSchema = (optional = false) =>
  z
    .array(
      z.object({
        ...createComponentBaseSchema("button").shape,
        label: z.string(),
        anchor: z.string(),
      })
    )
    .max(1)
    .min(optional ? 0 : 1)
    .default([])
    .transform((x) => x[0]);

const createHeroBlockSchema = () =>
  z.object({
    ...createComponentBaseSchema("hero").shape,
    anchor: z.string().optional(),
    image: createAssetSchema(),
    image_mobile: createAssetSchema(),
    title: z.string().min(1),
    content: createRichTextSchema(),
    button1: createButtonBaseSchema(true),
    button2: createButtonBaseSchema(true),
    branch: z.string().optional(),
    location: z.string().optional(),
    hours: z.string().optional(),
    salary: z.string().optional(),
    education: z.string().optional(),
    experience: z.string().optional(),
    background_color: createColorSchema(),
    text_color: createColorSchema(),
  });

const createTextBlockSchema = () =>
  z.object({
    ...createComponentBaseSchema("text-block").shape,
    anchor: z.string().optional(),
    title: z.string().optional(),
    content: createRichTextSchema(),
    section_width: z.string().optional(),
  });

const createJobOfferIntroBlockSchema = () =>
  z.object({
    ...createComponentBaseSchema("job-offer-intro").shape,
    anchor: z.string().optional(),
    title: z.string().optional(),
    content: z.string().optional(),
    left_title: z.string().optional(),
    right_title: z.string().optional(),
    left_content: createRichTextSchema(),
    right_content: createRichTextSchema(),
    block_background_color: createColorSchema(),
    section_width: z.string().optional(),
  });

const createContactPersonBlockSchema = () =>
  z.object({
    ...createComponentBaseSchema("contact-person").shape,
    anchor: z.string().optional(),
    title: z.string(),
    content: createRichTextSchema(),
    video: createAssetSchema()
      .extend({
        filename: z.string().url().nullable().optional(),
      })
      .optional(),
    video_poster: createAssetSchema(),
    avatar: createAssetSchema().optional(),
    email: z.string().email().optional().or(z.literal("")),
    phone: z.string().optional(),
    whatsapp: z.string().url().optional().or(z.literal("")),
    background_color: createColorSchema(),
    section_width: z.string().optional(),
  });

const createWorkAtBlockSchema = () =>
  z.object({
    ...createComponentBaseSchema("work-at").shape,
    anchor: z.string().optional(),
    title: z.string(),
    content: createRichTextSchema(),
    video: createAssetSchema()
      .extend({
        filename: z.string().url().nullable().optional(),
      })
      .optional(),
    video_poster: createAssetSchema()
      .extend({
        filename: z.string().url().nullable().optional(),
      })
      .optional(),
    location_title: z.string().optional(),
    location_text: z.string().optional(),
    employees_title: z.string().optional(),
    employees_text: z.string().optional(),
    gender_rate_title: z.string().optional(),
    gender_rate_text: z.string().optional(),
    age_title: z.string().optional(),
    age_text: z.string().optional(),
    dresscode_title: z.string().optional(),
    dresscode_text: z.string().optional(),
    work_from_title: z.string().optional(),
    work_from_text: z.string().optional(),
    button: createButtonBaseSchema(true),
    background_color: createColorSchema(),
    section_width: z.string().optional(),
  });

const createApplicationProcessBlockSchema = () =>
  z.object({
    ...createComponentBaseSchema("application-process").shape,
    anchor: z.string().optional(),
    title: z.string(),
    content: z.string().optional(),
    cv_label: z.string().optional(),
    recruiter_label: z.string().optional(),
    colleagues_label: z.string().optional(),
    assessment_label: z.string().optional(),
    success_label: z.string().optional(),
    button: createButtonBaseSchema(true),
    background_color: createColorSchema(),
    icon_color: createColorSchema(),
    icon_background_color: createColorSchema(),
    section_width: z.string().optional(),
  });

const createColleaguesBlockSchema = () =>
  z.object({
    ...createComponentBaseSchema("colleagues").shape,
    anchor: z.string().optional(),
    title: z.string(),
    colleagues: z
      .array(
        z.object({
          ...createComponentBaseSchema("colleague").shape,
          name: z.string(),
          role: z.string().optional(),
          image: createAssetSchema(),
          quote: z.string(),
          background_color: createColorSchema(),
        })
      )
      .min(1),
  });

const createClientsBlockSchema = () =>
  z.object({
    ...createComponentBaseSchema("clients").shape,
    anchor: z.string().optional(),
    title: z.string(),
    clients: z
      .array(
        z.object({
          ...createComponentBaseSchema("client").shape,
          logo: createAssetSchema(),
        })
      )
      .min(1),
    background_color: createColorSchema(),
  });

const createApplicationFormBlockSchema = () =>
  z.object({
    ...createComponentBaseSchema("application-form").shape,
    anchor: z.string().optional(),
    title: z.string(),
    content: createRichTextSchema(),
    image: createAssetSchema().optional(),
    recruiter_email: z.string().email(),
    show_name: z.boolean(),
    show_email: z.boolean(),
    show_phone: z.boolean(),
    show_cv: z.boolean(),
    show_motivation: z.boolean(),
    show_remarks: z.boolean(),
    background_color: createColorSchema(),
    section_width: z.string().optional(),
  });

const createRecruiteeApplicationFormBlockSchema = () =>
  z.object({
    ...createComponentBaseSchema("recruitee-application-form").shape,
    anchor: z.string().optional(),
    title: z.string(),
    content: createRichTextSchema(),
    company_slug: z.string(),
    job_offer_slug: z.string(),
    background_color: createColorSchema(),
    section_width: z.string().optional(),
  });

const createQuoteBlockSchema = () =>
  z.object({
    ...createComponentBaseSchema("quote").shape,
    anchor: z.string().optional(),
    quote: z.string(),
    image: createAssetSchema(),
    button: createButtonBaseSchema(true),
    background_color: createColorSchema(),
    section_width: z.string().optional(),
  });

const createCollapsibleContentBlockSchema = () =>
  z.object({
    ...createComponentBaseSchema("collapsible-content").shape,
    anchor: z.string().optional(),
    title: z.string(),
    intro: createRichTextSchema(),
    content: createRichTextSchema(),
    image: createAssetSchema(),
    background_color: createColorSchema(),
    section_width: z.string().optional(),
  });

const createCarreerPathBlockSchema = () =>
  z.object({
    ...createComponentBaseSchema("carreer-path").shape,
    anchor: z.string().optional(),
    title: z.string(),
    content: createRichTextSchema(),
    steps: z
      .array(
        z.object({
          ...createComponentBaseSchema("carreer-path-step").shape,
          title: z.string(),
          content: z.string(),
        })
      )
      .min(1)
      .max(3),
    section_width: z.string().optional(),
  });

const createSpacerBlockSchema = () =>
  z.object({
    ...createComponentBaseSchema("spacer").shape,
  });

const createJobOffersOverviewBlockSchema = () =>
  z.object({
    ...createComponentBaseSchema("job-offers-overview").shape,
    anchor: z.string().optional(),
    title: z.string(),
    content: createRichTextSchema(),
    open_label: z.string(),
    items: z.array(
      z.object({
        ...createComponentBaseSchema("job-offers-overview-item").shape,
        avatar: createAssetSchema(),
        logo: createAssetSchema(),
        title: z.string(),
        excerpt: z.string(),
        link: createLinkSchema(),
      })
    ),
    background_color: createColorSchema(),
    section_width: z.string().optional(),
  });

const createFooterBlockSchema = () =>
  z.object({
    ...createComponentBaseSchema("footer").shape,
    anchor: z.string().optional(),
    logo: createAssetSchema(),
    column1_title: z.string().optional(),
    column1_items: z.array(createMenuItemSchema()),
    column2_title: z.string().optional(),
    column2_items: z.array(createMenuItemSchema()),
    column3_title: z.string().optional(),
    column3_items: z.array(createMenuItemSchema()),
    column4_title: z.string().optional(),
    column4_items: z.array(createMenuItemSchema()),
    copyright: z.string(),
    socket_items: z.array(createMenuItemSchema()),
    background_color: createColorSchema(),
    text_color: createColorSchema(),
  });

export const createLandingspageSchema = () =>
  z.object({
    ...createComponentBaseSchema("landingspage").shape,
    seo: createSeoSchema(),
    job_posting: createJobPostingSchema(),
    gtm: createGtmSchema(),
    primary_color: createColorSchema(),
    header_background_color: createColorSchema(),
    special_class: z.string().optional(),
    logo: createAssetSchema(),
    cta: createButtonBaseSchema(true),
    menu_items: z.array(createMenuItemSchema()).default([]),
    body: z
      .array(
        z.discriminatedUnion("component", [
          createHeroBlockSchema(),
          createJobOfferIntroBlockSchema(),
          createTextBlockSchema(),
          createContactPersonBlockSchema(),
          createWorkAtBlockSchema(),
          createApplicationProcessBlockSchema(),
          createColleaguesBlockSchema(),
          createClientsBlockSchema(),
          createApplicationFormBlockSchema(),
          createRecruiteeApplicationFormBlockSchema(),
          createQuoteBlockSchema(),
          createCollapsibleContentBlockSchema(),
          createCarreerPathBlockSchema(),
          createSpacerBlockSchema(),
          createJobOffersOverviewBlockSchema(),
          createFooterBlockSchema(),
        ])
      )
      .default([]),
  });

export const createStorySchema = <T extends ZodRawShape>(
  storyTypeSchema: ZodObject<T>
) =>
  z.object({
    name: z.string(),
    created_at: z.string().datetime(),
    published_at: z
      .string()
      .datetime()
      .nullish()
      .transform((x) => x ?? undefined),
    id: z.number().int(),
    uuid: z.string().uuid(),
    content: storyTypeSchema,
    slug: z.string(),
    full_slug: z.string(),
  });

type SchemaBuilderToSchema<T extends () => ZodType<any, any, any>> = z.infer<
  ReturnType<T>
>;

export type LandingspageData = SchemaBuilderToSchema<
  typeof createLandingspageSchema
>;

export type StoryData<T> = z.infer<ReturnType<typeof createStorySchema<{}>>> & {
  content: T;
};

export type HeroBlockData = SchemaBuilderToSchema<typeof createHeroBlockSchema>;
export type TextBlockData = SchemaBuilderToSchema<typeof createTextBlockSchema>;
export type JobOfferIntroBlockData = SchemaBuilderToSchema<
  typeof createJobOfferIntroBlockSchema
>;
export type ContactPersonBlockData = SchemaBuilderToSchema<
  typeof createContactPersonBlockSchema
>;
export type WorkAtBlockData = SchemaBuilderToSchema<
  typeof createWorkAtBlockSchema
>;
export type ApplicationProcessBlockData = SchemaBuilderToSchema<
  typeof createApplicationProcessBlockSchema
>;
export type ColleaguesBlockData = SchemaBuilderToSchema<
  typeof createColleaguesBlockSchema
>;
export type ClientsBlockData = SchemaBuilderToSchema<
  typeof createClientsBlockSchema
>;
export type ApplicationFormBlockData = SchemaBuilderToSchema<
  typeof createApplicationFormBlockSchema
>;
export type RecruiteeApplicationFormBlockData = SchemaBuilderToSchema<
  typeof createRecruiteeApplicationFormBlockSchema
>;
export type QuoteBlockData = SchemaBuilderToSchema<
  typeof createQuoteBlockSchema
>;
export type CollapsibleContentBlockData = SchemaBuilderToSchema<
  typeof createCollapsibleContentBlockSchema
>;
export type CarreerPathBlockData = SchemaBuilderToSchema<
  typeof createCarreerPathBlockSchema
>;
export type SpacerBlockData = SchemaBuilderToSchema<
  typeof createSpacerBlockSchema
>;
export type JobOffersOverviewData = SchemaBuilderToSchema<
  typeof createJobOffersOverviewBlockSchema
>;
export type FooterBlockData = SchemaBuilderToSchema<
  typeof createFooterBlockSchema
>;

export type AssetData = SchemaBuilderToSchema<typeof createAssetSchema>;
export type LinkData = SchemaBuilderToSchema<typeof createLinkSchema>;
export type MenuItemData = SchemaBuilderToSchema<typeof createMenuItemSchema>;
export type JobPostingData = SchemaBuilderToSchema<
  typeof createJobPostingSchema
>;
